import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue';
import type { Props, TypeClasses, Types } from '../types';
import { durations } from '../utils';

export default function useTransitionBase(props: MaybeRefOrGetter<Props>) {
  const _props = toValue(props);

  const durationClass = computed(() => durations[_props.duration!]);

  const typeClasses: ComputedRef<Record<Types, TypeClasses>> = computed(() => ({
    fadeInSlideLeft: {
      active: `transition ${durationClass.value}`,
      invisible: 'opacity-0 translate-x-8',
      visible: '',
    },
    fadeInSlideRight: {
      active: `transition ${durationClass.value}`,
      invisible: 'opacity-0 -translate-x-8',
      visible: '',
    },
    flip: {
      active: `transition ${durationClass.value}`,
      invisible: '-scale-y-100 opacity-0',
      visible: '',
    },
    slideDown: {
      active: `grid transition-all ${durationClass.value} *:overflow-hidden`,
      invisible: 'grid-rows-[0fr]',
      visible: 'grid-rows-[1fr]',
    },
    slideRight: {
      active: `grid transition-all ${durationClass.value} *:overflow-hidden`,
      invisible: 'grid-cols-[0fr]',
      visible: 'grid-cols-[1fr]',
    },
    slideDownTranslate: {
      active: `transition-all ${durationClass.value} fixed inset-0 z-overlay`,
      invisible: '-translate-y-5 opacity-0',
      visible: 'translate-y-0',
    },
  }));

  const typeNeedsContainers: Record<Types, boolean> = {
    fadeInSlideLeft: false,
    fadeInSlideRight: false,
    flip: false,
    slideDown: true,
    slideDownTranslate: true,
    slideRight: true,
  };

  const enterType = computed(() =>
    typeof _props.type === 'object' ? _props.type.enter : _props.type,
  );
  const leaveType = computed(() =>
    typeof _props.type === 'object' ? _props.type.leave : _props.type,
  );

  const classAttributes = computed(() => ({
    ...(enterType.value && {
      'enter-active-class': typeClasses.value[enterType.value].active,
      'enter-from-class': typeClasses.value[enterType.value].invisible,
      'enter-to-class': typeClasses.value[enterType.value].visible,
    }),
    ...(leaveType.value && {
      'leave-active-class': typeClasses.value[leaveType.value].active,
      'leave-to-class': typeClasses.value[leaveType.value].invisible,
      'leave-from-class': typeClasses.value[leaveType.value].visible,
    }),
  }));

  const needsContainers = computed(() =>
    enterType.value ? typeNeedsContainers[enterType.value] : false,
  );

  return {
    classAttributes,
    needsContainers,
  };
}
